// Deterministic fallback evaluator: maps the extracted metrics to findings using
// the thresholds in the criteria JSON. Always available (no WebGPU needed).

const BAND_RANK = { low: 0, moderate: 1, high: 2 };
const SEVERITY_FOR_BAND = { low: 0.2, moderate: 0.5, high: 0.85 };

export function humanizeRegion(region) {
  return (region || '')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toLowerCase())
    .trim();
}

function bandFor(bands, value) {
  for (const b of bands) if (value <= b.max) return b.band;
  return bands[bands.length - 1].band;
}

function fillTemplate(tpl, vars) {
  return (tpl || '').replace(/\{(\w+)\}/g, (_, k) => (k in vars ? vars[k] : `{${k}}`));
}

// `{product}` is left as a token for the UI to replace with the resolved product
// name (names live in the product catalog, not here).
//
// `positions` (normalized region coords) drive the hotspots; `intake.concerns`
// (what the user said they care about) gently boost matching concerns so the
// recommendations line up with their stated goals.
export function ruleEvaluate(metrics, criteria, positions, intake) {
  const selected = new Set((intake && intake.concerns) || []);
  const pos = positions || {};

  const scored = criteria.concerns.map((concern) => {
    let worst = null;
    let worstVal = -1;
    for (const region of concern.regions) {
      const v = (metrics[region] && metrics[region][concern.metric]) || 0;
      if (v > worstVal) {
        worstVal = v;
        worst = region;
      }
    }
    // A user-stated concern is treated as at least moderate.
    const severity = selected.has(concern.id) ? Math.max(worstVal, 0.5) : worstVal;
    return { concern, worst, severity };
  });

  const toFinding = ({ concern, worst, severity }) => {
    const band = bandFor(concern.severityBands, severity);
    return {
      concernId: concern.id,
      label: concern.label,
      region: worst,
      severity,
      band,
      position: pos[worst] || null,
      rationale: fillTemplate(concern.explanationTemplate, {
        band,
        regions: humanizeRegion(worst),
        product: '{product}',
      }),
      productSkus: concern.productSkus || [],
      tanningContent: concern.tanningContent || [],
    };
  };

  let findings = scored
    .filter((s) => BAND_RANK[bandFor(s.concern.severityBands, s.severity)] >= 1)
    .map(toFinding);

  // Always surface something to recommend: if nothing crossed the bar, take the
  // single strongest signal.
  if (findings.length === 0) {
    const top = [...scored].sort((a, b) => b.severity - a.severity)[0];
    if (top) findings = [toFinding(top)];
  }

  findings.sort((a, b) => b.severity - a.severity);
  findings = findings.slice(0, 4);

  const summary = findings.length
    ? `Here are ${findings.length} area${findings.length > 1 ? 's' : ''} we'd focus on for your best glow.`
    : 'Your skin is looking great — keep it up!';
  return { findings, summary, engine: 'rules' };
}

// Shared helper so the WebLLM path can reuse band->severity mapping.
export function severityForBand(band) {
  return SEVERITY_FOR_BAND[band] != null ? SEVERITY_FOR_BAND[band] : 0.5;
}
